import { useEffect, useMemo, useState } from 'react';
import { createFrameScheduler } from '@/services/chatStream/frameScheduler';

/**
 * How much of the layout viewport the on-screen keyboard is covering.
 *
 * `window.innerHeight` is the layout viewport and does not move when the
 * keyboard opens; `visualViewport` is what the reader can actually see. The
 * difference, minus however far the visual viewport has been pushed down, is
 * the occluded strip at the bottom — the strip the composer would otherwise
 * be sitting under.
 */
export const readVisualViewportInset = (
  viewport: VisualViewport | undefined,
  innerHeight: number
): number => {
  if (!viewport) return 0;
  return Math.max(0, innerHeight - viewport.height - viewport.offsetTop);
};

/**
 * The keyboard-occluded height at the bottom of the window, in pixels
 * (ORA-599).
 *
 * Zero wherever there is no `visualViewport` — jsdom, older browsers, and
 * every desktop case that never opens a keyboard — so a caller can apply it
 * unconditionally and get nothing on the surfaces that do not need it.
 *
 * Measurements are coalesced through the chat frame scheduler: iOS fires
 * `resize` and `scroll` together, several times, as the keyboard animates in.
 */
const useVisualViewportInset = (): number => {
  const [inset, setInset] = useState(0);
  const frames = useMemo(() => createFrameScheduler(), []);

  useEffect(() => {
    const viewport =
      typeof window === 'undefined' ? undefined : window.visualViewport;

    if (!viewport) return undefined;

    const measure = () => {
      frames.schedule(() => {
        setInset(readVisualViewportInset(viewport, window.innerHeight));
      });
    };

    measure();
    viewport.addEventListener('resize', measure);
    viewport.addEventListener('scroll', measure);

    return () => {
      frames.cancel();
      viewport.removeEventListener('resize', measure);
      viewport.removeEventListener('scroll', measure);
    };
  }, [frames]);

  return inset;
};

export default useVisualViewportInset;
